import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import menuService from "@services/menuService";
import { Box, Checkbox, Grid, Stack } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import ListCard, { AssetInformation } from "@components_v2/card/list-card";
import { Link } from "react-router-dom";
import {
    IconAi,
    IconCheckDefault,
    IconDrm,
    IconLink,
    IconLock,
    IconMoreHoriz,
    IconStar,
    IconVoice,
} from "@mzc-cloudplex/icons";
import { Menu } from "@components_v2/menu";
import AssetThumbnail from "@features/thumbnail/asset-thumbnail";
import { ARCHIVED_ASSET_STATUS, ASSET_PREVIEW_STATUS, MEDIA_TYPES, MENU_LIST } from "@constants";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import useCheckPermission from "@hooks/useCheckPermission";
import { useLocation } from "react-router";
import countDaysBetweenDates from "@cores/countDaysBetweentDates";
import dayjs from "dayjs";
import compareVersions, {
    VERSION_CM_13766_SHARES_V3,
    VERSION_CM_14143_REQUEST,
    VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
} from "@cores/version";
import { CircledIcon } from "@components_v2/icon";
import { siteMode } from "@cores/siteMode";
import Tooltip from "@components_v2/Tooltip/tooltip";
import Typography from "@components_v2/typography/typography";
import useSettings from "@hooks/useSettings";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import { canNavigateToArchiveDetail, isArchiveRelatedStatus } from "@utils/archive-utils";
import { OverflowTooltip } from "@components_v2/ellipsis-tooltip";

const CardLinkedAsset = ({
    data,
    primaryAssetId,
    selected,
    thumbnailCrop,
    onClickCheck,
    onClick,
    onDoubleClick,
    onToggleModal,
    onClickDownload,
    onClickSetAsDefault,
    onClickName,
    onClickStarred,
}) => {
    const history = useHistory();
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const isCms = siteMode.get() === "CMS";
    const { navigatorMenus } = useSettings({ type: "General" });

    const COLLECTIONS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });

    const { stage, project, myProjectPermissions } = useSelector(({ stage, project, roleManagementV2 }) => ({
        stage,
        project,
        myProjectPermissions: roleManagementV2?.projectPermissions?.data,
    }));

    const commonStatusPreviews = data.previews?.filter((p) => p.status !== ASSET_PREVIEW_STATUS.ERROR);
    let src = commonStatusPreviews?.[0]?.accessUrl;
    if (!src) {
        if (data?.mediaType === MEDIA_TYPES.IMAGE) {
            src = data.accessUrls?.[0] ?? data.elements?.[0]?.accessUrl;
        } else if (data.mediaType === MEDIA_TYPES.VIDEO) src = data.thumbnails?.[0]?.accessUrl;
    }

    const previewStatus = useMemo(() => {
        // NOTE : 모든 프리뷰가 Error일 경우에만 Error로 표시
        if (commonStatusPreviews?.length === 0 && data.previews?.length > 0) return ASSET_PREVIEW_STATUS.ERROR;
        else return commonStatusPreviews?.[0]?.status ?? ASSET_PREVIEW_STATUS.COMPLETE;
    }, [data, commonStatusPreviews]);

    const isOwner = useMemo(() => {
        return compareVersions(stage.version, VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR) < 0
            ? data.created?.by?.id
            : data.owner?.by?.id === stage.user?.id;
    }, [stage, data]);

    const disabledByOwnerLock = useMemo(() => {
        return data?.isOwnerLocked && !isOwner;
    }, [data, isOwner]);

    const hasDownloadPermissionByShare = useMemo(() => {
        const isExpired = data?.shareInfo?.expiresAt
            ? countDaysBetweenDates({ toDate: dayjs(data?.shareInfo?.expiresAt) }) < 0
            : false;

        const isExceedDownloadCount =
            data?.shareInfo?.maxDownloads > 0 && data?.shareInfo?.maxDownloads <= data?.shareInfo?.downloadCount;

        return data?.shareInfo && !(isExpired || isExceedDownloadCount);
    }, [data.shareInfo]);

    const hiddenDownloadButton =
        (!data?.shareInfo || !hasDownloadPermissionByShare) && (!DOWNLOAD_PERMISSIONS.DOWNLOAD || disabledByOwnerLock);

    const menuList = useMemo(() => {
        return [
            {
                label: t("common::label::Set as default", "Set as default"),
                onClick: (n, value) => {
                    onClickSetAsDefault(value.id);
                },
                clickClose: true,
                disabled: !COLLECTIONS_PERMISSIONS.EDIT,
                hidden: isCms,
            },
            {
                label: t("common::label::Share link", "Share link"),
                onClick: (n, value) => {
                    onToggleModal("shareLink", true, value);
                },
                divider: true,
                disabled:
                    isArchiveRelatedStatus(data.archiveStatus) || !SHARES_PERMISSIONS.EXTERNAL || disabledByOwnerLock,
                hidden: isCms,
                clickClose: true,
            },
            {
                label: t("common::label::Share request", "Share request"),
                onClick: (n, value) => {
                    onToggleModal("requestShare", true, value);
                },
                disabled: isArchiveRelatedStatus(data.archiveStatus) || !SHARES_PERMISSIONS.INTERNALREQUEST,
                hidden: compareVersions(stage.version, VERSION_CM_13766_SHARES_V3) >= 0,
                clickClose: true,
            },
            {
                label: t(`common::label::Request`, `Request`),
                onClick: (_, value) => {
                    onToggleModal("request", true, value);
                },
                hidden: compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0,
                disabled: isArchiveRelatedStatus(data.archiveStatus) || !SHARES_PERMISSIONS.INTERNALREQUEST,
                clickClose: true,
            },
            {
                label: t("common::label::Rename", "Rename"),
                onClick: (n, value) => {
                    onToggleModal("rename", true, value);
                },
                disabled:
                    isArchiveRelatedStatus(data.archiveStatus) || !COLLECTIONS_PERMISSIONS.EDIT || disabledByOwnerLock,
                hidden: isCms,
                divider: true,
                clickClose: true,
            },
            {
                label: t("common::label::Download", "Download"),
                onClick: (n, value) => {
                    onClickDownload(value);
                },
                disabled:
                    isArchiveRelatedStatus(data.archiveStatus) ||
                    (compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 && !DOWNLOAD_PERMISSIONS.DOWNLOAD),
                hidden: compareVersions(stage.version, VERSION_CM_14143_REQUEST) >= 0 && hiddenDownloadButton,
                divider: true,
                clickClose: true,
            },
            {
                label: t(`common::label::Request download`, `Request download`),
                onClick: (_, value) => {
                    onToggleModal("requestDownload", true, value);
                },
                hidden: compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 || !hiddenDownloadButton,
                disabled: isArchiveRelatedStatus(data.archiveStatus) || !SHARES_PERMISSIONS.INTERNALREQUEST,
                clickClose: true,
            },
            {
                label: t(
                    `common::label::${data?.myFavorite ? "Unstarred" : "Starred"}`,
                    data?.myFavorite ? "Unstarred" : "Starred",
                ),
                onClick: () => {
                    onClickStarred([data.id], data.myFavorite);
                },
                disabled: data.state === "DRAFT",
                clickClose: true,
            },
            {
                label: t("common::label::Remove", "Remove"),
                onClick: (n, value) => {
                    onToggleModal("delete", true, value);
                },
                disabled: !COLLECTIONS_PERMISSIONS.EDIT,
                hidden: isCms,
                divider: true,
                clickClose: true,
            },
        ];
    }, [isCms, myProjectPermissions, onClickSetAsDefault, onClickStarred, onClickDownload]);

    const onClickOpenPopup = () => {
        let url = "";
        if (canNavigateToArchiveDetail(data.archiveStatus))
            url = menuService.paths.archiveAssetDetail(stage.id, project.id, data.id);
        else url = menuService.paths.assetsAssetDetail(stage.id, project.id, data.id);

        return `${url}?projectId=${data.project.id}`;
    };

    return (
        <ListCard
            thumbnail={
                <Box>
                    <AssetThumbnail
                        width={202}
                        height={202}
                        variant={`card`}
                        src={src ?? ""}
                        mediaType={data.mediaType}
                        thumbnailCrop={thumbnailCrop}
                        isOwnerLocked={data.isOwnerLocked}
                        archiveStatus={data.archiveStatus}
                        createdAt={data.createdAt ?? data.created?.at}
                        showIcons={true}
                        disableHover={false}
                        asset={data}
                        previewStatus={previewStatus}
                        errorIconProps={{ position: "right", size: 16 }}
                        hasCheckbox={true}
                    />
                </Box>
            }
            checkbox={
                <Checkbox
                    color={"greySecondary"}
                    checked={selected?.find(({ id }) => id === data.id) !== undefined}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    onChange={(e) => {
                        e.stopPropagation();
                        onClickCheck(data, e.target.checked);
                    }}
                />
            }
            name={{
                startIcon: <AssetTypeFlag size={"small"} type={data.mediaType} />,
                name:
                    canNavigateToArchiveDetail(data.archiveStatus) && !navigatorMenus?.[MENU_LIST.ARCHIVED]?.visible ? (
                        <Tooltip
                            title={
                                // 이 분기는 링크가 아니라 사유 안내가 뜬다. 이름이 잘려도 읽을 방법이 없어
                                // 같은 툴팁 첫 줄에 이름을 함께 담는다. (툴팁이 두 개가 되지 않게)
                                <>
                                    <Box component={"span"} sx={{ display: "block", fontWeight: 500 }}>
                                        {data.name}
                                    </Box>
                                    {t(
                                        `common::msg::It's not possible to proceed as the archived menu isn't being used.`,
                                        `It's not possible to proceed as the archived menu isn't being used.`,
                                    )}
                                </>
                            }
                        >
                            <Box
                                // width:fit-content 이면 부모 폭을 넘어도 줄어들지 않아 말줄임이 걸리지 않는다.
                                // 잘림 표시는 이 Box 가 맡는다. (DAM-654 · PR #531 리뷰 반영)
                                sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                // 툴팁 title 이 문자열이 아니면 MUI 가 aria-label 을 넣지 않는다(열렸을 때만 aria-labelledby).
                                // 이름과 사유를 두 줄로 보여주면서 접근 가능한 이름은 여기서 직접 준다.
                                aria-label={`${data.name} — ${t(
                                    `common::msg::It's not possible to proceed as the archived menu isn't being used.`,
                                    `It's not possible to proceed as the archived menu isn't being used.`,
                                )}`}
                            >
                                <Typography variant={"body1_500"} component={"span"} noWrap>
                                    {data.name}
                                </Typography>
                            </Box>
                        </Tooltip>
                    ) : (
                        <OverflowTooltip title={data.name}>
                            <Link
                                style={{
                                    color: theme.palette.greySecondary.main,
                                    fontWeight: 500,
                                    // 20자 하드 절단 대신 카드 폭에 맞춰 자른다.
                                    // 잘림 여부를 이 요소에서 측정하므로 말줄임 스타일을 직접 지정한다.
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                to={onClickName(data)}
                            >
                                {data.name}
                            </Link>
                        </OverflowTooltip>
                    ),
                badge: (
                    <Stack direction={"row"} gap={theme.spacing(0.5)}>
                        {data.isConnected ? <IconLink size={16} /> : ""}
                        {data.myFavorite ? <IconStar size={16} htmlColor={theme.palette.etc.orange500} /> : ""}
                    </Stack>
                ),
            }}
            subName={{
                name: <AssetInformation extensions={data.extensions} size={data.size} />,
                icons: (
                    <Stack direction={"row"} gap={theme.spacing(0.5)} sx={{ "& i.sprite + *": { ml: 0 } }}>
                        {data?.hasDubbing && (
                            <CircledIcon size={20} color={theme.palette.etc.purple400}>
                                <IconVoice size={12} htmlColor={theme.palette.constant.white} />
                            </CircledIcon>
                        )}
                        {data?.encryptions?.indexOf("DRM") > -1 && (
                            <CircledIcon size={20} color={theme.palette.warning.main}>
                                <IconDrm size={12} htmlColor={theme.palette.constant.white} />
                            </CircledIcon>
                        )}
                        {data?.isAnalyzed && (
                            <CircledIcon size={20} color={theme.palette.success.light}>
                                <IconAi size={12} htmlColor={theme.palette.constant.white} />
                            </CircledIcon>
                        )}
                        {data?.isOwnerLocked && (
                            <CircledIcon size={20} color={theme.palette.etc.gray300}>
                                <IconLock size={12} htmlColor={theme.palette.constant.white} />
                            </CircledIcon>
                        )}
                    </Stack>
                ),
            }}
            actions={
                <Menu
                    menuMinWidth={160}
                    placement={"right"}
                    type={"icon"}
                    label={<IconMoreHoriz size={16} />}
                    size={"small"}
                    value={data}
                    list={menuList}
                    useInCard={true}
                />
            }
            onClickCard={() => onClick(data)}
            onDoubleClickCard={() => onDoubleClick && onDoubleClick(data)}
        />
    );
};

export default CardLinkedAsset;
